import { useEffect, useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Modal, Button, Form, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "sonner";

import axios from "axios";
import CustomDropdown from "./CustomDropdown";
import nodeApi from "../lib/api";

// Environment variables for ALERP API
const alerpApiUrl = process.env.REACT_APP_ALERP_API_URL;
const alerpApiKey = process.env.REACT_APP_ALERP_API_KEY;

const forFactory = {
  factorySource: "ALCLAD",
  factoryDestination:
    "PS Pace 10TH FLOOR, UNIT- 1003 1/1A MAHENDRA ROY LANE KOLKATA – 700046",
};

const initialChallan = () => ({
  og_ch_no: "",
  og_ch_date: "",
  og_from: "Factory",
  og_source_name: "",
  og_source_id: "",
  og_source_address: "",
  og_to: "Coaters",
  og_destination_name: "",
  og_destination_id: "",
  og_destination_address: "",
  min_no: "",
  min_date: "",
  pin_date: "",
  og_weight_kg: "",
  items: [
    {
      item_code: "",
      alclad_code: "",
      item_description: "",
      size: "",
      quantity: "",
      og_color_shade: "",
    },
  ],
});

function EditOutgoingDialog({ show, onClose, challan }) {
  const [formData, setFormData] = useState(initialChallan());
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const queryClient = useQueryClient();

  // Fetch ALERP suppliers
  const { data: suppliers, isLoading: suppliersLoading } = useQuery({
    queryKey: ["alerpSuppliers"],
    queryFn: async () => {
      const res = await nodeApi.get(`${alerpApiUrl}/challan_suppliers`, {
        headers: { "X-API-Key": alerpApiKey },
      });
      return res.data.data;
    },
    enabled: !!(alerpApiUrl && alerpApiKey),
    onError: (err) => {
      toast.error(`Failed to fetch ALERP suppliers: ${err.message}`);
    },
  });

  // Fetch coaters
  const { data: coaters, isLoading: coatersLoading } = useQuery({
    queryKey: ["coaters"],
    queryFn: async () => {
      const res = await nodeApi.get("/coater");
      return res.data;
    },
  });

  // Fetch item names from ALERP API
  const itemApiUrl =
    process.env.REACT_APP_ALERP_API_URL || "https://alcad-erp-api.sirpify.com";
  const { data: itemNames = [], isLoading: itemNamesLoading } = useQuery({
    queryKey: ["itemNames"],
    queryFn: async () => {
      try {
        const res = await axios.get(`${itemApiUrl}/item_names_challan`, {
          headers: { "X-API-Key": alerpApiKey },
        });
        let items = [];
        if (res.data?.data && Array.isArray(res.data.data)) {
          items = res.data.data;
        } else if (Array.isArray(res.data)) {
          items = res.data;
        } else if (res.data?.items && Array.isArray(res.data.items)) {
          items = res.data.items;
        } else if (res.data?.results && Array.isArray(res.data.results)) {
          items = res.data.results;
        }

        const normalizedItems = items.map((item, idx) => {
          const itemCode =
            item.item_code ||
            item.code ||
            item.ItemCode ||
            item.itemCode ||
            item.item_ref_code ||
            item.ref_code ||
            item.id ||
            "-";

          const itemDescription =
            item.item_desc ||
            item.item_description ||
            item.description ||
            item.ItemDescription ||
            item.itemDescription ||
            item.desc ||
            item.name ||
            item.item_name ||
            item.itemName ||
            "-";

          const alcladCode = item.alclad_code || "-";

          const normalized = {
            ...item,
            item_code: String(itemCode || "").trim(),
            alclad_code: String(alcladCode || "").trim(),
            item_description: String(itemDescription || "").trim(),
            item_desc: String(item.item_desc || itemDescription || "").trim(),
          };
          return normalized;
        });
        // Removed filter to show all data including items with "-"

        return normalizedItems;
      } catch (error) {
        console.error("Error fetching item names:", error);
        console.error("Error response:", error.response?.data);
        throw error;
      }
    },
    onError: (err) => {
      toast.error(`Failed to fetch item names: ${err.message}`);
    },
  });

  // Pre-populate form with existing challan data
  useEffect(() => {
    // Only run if the modal is shown and we have challan data
    if (show && challan) {
      setIsDirty(false);
      const updatedFormData = {
        ...initialChallan(),
        ...challan,
        // Ensure IDs are strings for the dropdown component to match correctly
        og_source_id: String(challan.og_source_id || ""),
        og_destination_id: String(challan.og_destination_id || ""),

        // Format dates correctly for the date input fields
        og_ch_date: challan.og_ch_date
          ? new Date(challan.og_ch_date).toISOString().split("T")[0]
          : "",
        min_date: challan.min_date
          ? new Date(challan.min_date).toISOString().split("T")[0]
          : "",
        pin_date: challan.pin_date
          ? new Date(challan.pin_date).toISOString().split("T")[0]
          : "",

        // Ensure items are properly mapped and quantities are strings for the form inputs
        items:
          challan?.outgoing_items?.length > 0
            ? challan.outgoing_items.map((item) => ({
                ...item,
                alclad_code: item.alclad_code || "",
                quantity: String(item.quantity || ""),
              }))
            : initialChallan().items,
      };

      // --- POPULATION LOGIC ---

      // 1. Populate SOURCE details (Factory vs. Supplier)
      if (updatedFormData.og_from === "Factory") {
        updatedFormData.og_source_name = forFactory.factorySource;
        updatedFormData.og_source_address = forFactory.factoryDestination;
        updatedFormData.og_source_id = "";
      } else if (
        updatedFormData.og_from === "Supplier" &&
        suppliers &&
        updatedFormData.og_source_id
      ) {
        // If suppliers data is ready, find the matching supplier
        const selectedSupplier = suppliers.find(
          (s) => String(s.supplier_code) === updatedFormData.og_source_id
        );
        if (selectedSupplier) {
          updatedFormData.og_source_name = selectedSupplier.supplier_name;
          updatedFormData.og_source_address = [
            selectedSupplier.ho_address,
            selectedSupplier.local_address,
          ]
            .filter(Boolean)
            .join(", ");
        }
      }

      // 2. Populate DESTINATION details (Coater)
      // If coaters data is ready, find the matching coater by its ID
      if (coaters && updatedFormData.og_destination_id) {
        const selectedCoater = coaters.find(
          (c) => String(c.coater_id) === updatedFormData.og_destination_id
        );
        if (selectedCoater) {
          updatedFormData.og_destination_name = selectedCoater.name;
          updatedFormData.og_destination_address = selectedCoater.address;
        }
      }

      setFormData(updatedFormData);
      setErrors({});
    }
  }, [show, challan, coaters, suppliers]);

  // Update challan mutation
  const updateChallanMutation = useMutation({
    mutationFn: async (updatedChallan) => {
      const payload = {
        ...updatedChallan,
        og_weight_kg: updatedChallan.og_weight_kg
          ? Number(updatedChallan.og_weight_kg)
          : null,
        min_date: updatedChallan.min_date || null,
        pin_date: updatedChallan.pin_date || null,
        items: updatedChallan.items.map((item) => ({
          ...item,
          quantity: Number(item.quantity),
        })),
      };

      const res = await nodeApi.put(`/outgoing/${payload.id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Challan updated successfully");
      queryClient.invalidateQueries(["outgoing"]);
      onClose();
    },
    onError: (err) => {
      if (err.response?.data && typeof err.response.data === "object") {
        const backendErrors = err.response.data;
        const newErrors = {};
        const itemErrors = [];

        for (const [key, value] of Object.entries(backendErrors)) {
          if (key.startsWith("items.")) {
            const parts = key.split(".");
            const index = parseInt(parts[1], 10);
            const field = parts[2];
            while (itemErrors.length <= index) {
              itemErrors.push({});
            }
            if (value && value.length > 0) {
              itemErrors[index][field] = value[0];
            }
          } else {
            if (value && value.length > 0) {
              newErrors[key] = value[0];
            }
          }
        }
        if (itemErrors.length > 0) {
          newErrors.items = itemErrors;
        }
        setErrors(newErrors);
        toast.error("Please fix the validation errors and try again.");
      } else {
        const errMsg = err.message || "An unexpected error occurred";
        toast.error(errMsg);
      }
    },
  });

  const validate = () => {
    const newErrors = {};

    if (!(formData.og_ch_no || "").trim())
      newErrors.og_ch_no = "Challan No is required";
    if (!formData.og_ch_date)
      newErrors.og_ch_date = "Challan Date is required";
    if (!(formData.og_source_name || "").trim())
      newErrors.og_source_name = "Source Name is required";
    if (!(formData.og_destination_name || "").trim())
      newErrors.og_destination_name = "Destination Name is required";

    const itemErrors = formData.items.map((item, idx) => {
      const err = {};
      if (!(item.item_code || "").trim())
        err.item_code = `Item ${idx + 1}: Item Code required`;
      if (!(item.alclad_code || "").trim())
        err.alclad_code = `Item ${idx + 1}: Alclad Code required`;
      if (!(item.item_description || "").trim())
        err.item_description = `Item ${idx + 1}: Description required`;
      if (!(item.size || "").toString().trim())
        err.size = `Item ${idx + 1}: Size required`;
      if (!item.quantity)
        err.quantity = `Item ${idx + 1}: Quantity required`;
      if (!(item.og_color_shade || "").trim())
        err.og_color_shade = `Item ${idx + 1}: Color required`;
      return err;
    });

    if (itemErrors.some((err) => Object.keys(err).length > 0)) {
      newErrors.items = itemErrors;
    }

    return newErrors;
  };

  const handleSaveClick = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fill all the required fields.");
      return;
    }
    updateChallanMutation.mutate(formData);
  };

  const getInputClass = (field) =>
    errors[field] ? "form-control is-invalid" : "form-control";

  const getItemInputClass = (idx, field) =>
    errors.items?.[idx]?.[field] ? "form-control is-invalid" : "form-control";

  const handleItemChange = (index, field, value) => {
    const rawValue = value ?? "";
    let sanitizedValue = String(rawValue);
    if (field === "quantity") {
      sanitizedValue = sanitizedValue.replace(/[^\d]/g, "");
    }
    if (field === "size") {
      sanitizedValue = sanitizedValue.replace(/[^0-9.]/g, "");
      sanitizedValue = sanitizedValue.replace(/(\..*)\./g, "$1");
    }
    const trimmedValue = sanitizedValue.trim();

    setFormData((prev) => {
      setIsDirty(true);
      const updatedItems = prev.items.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: trimmedValue };

          // Auto-populate/reset item_description and alclad_code when item_code changes
          if (field === "item_code") {
            if (trimmedValue) {
              const selectedItem = itemNames?.find((itemOption) => {
                const code = String(itemOption.item_code || "").trim();
                return code === trimmedValue && code !== "";
              });
              if (selectedItem) {
                const desc =
                  selectedItem.item_desc || selectedItem.item_description || "";
                updatedItem.item_description = String(desc || "").trim();
                const alcladCode = selectedItem.alclad_code || "";
                updatedItem.alclad_code = String(alcladCode || "").trim();
              } else {
                updatedItem.item_description = "";
                updatedItem.alclad_code = "";
              }
            } else {
              updatedItem.item_description = "";
              updatedItem.alclad_code = "";
            }
          }

          // Auto-populate/reset item_code and alclad_code when item_description changes
          if (field === "item_description") {
            if (trimmedValue) {
              const selectedItem = itemNames?.find((itemOption) => {
                const itemDesc = String(
                  itemOption.item_desc || itemOption.item_description || ""
                ).trim();
                return itemDesc === trimmedValue && itemDesc !== "";
              });
              if (selectedItem && selectedItem.item_code) {
                updatedItem.item_code = String(selectedItem.item_code).trim();
                const alcladCode = selectedItem.alclad_code || "";
                updatedItem.alclad_code = String(alcladCode || "").trim();
              } else {
                updatedItem.item_code = "";
                updatedItem.alclad_code = "";
              }
            } else {
              updatedItem.item_code = "";
              updatedItem.alclad_code = "";
            }
          }

          // Auto-populate/reset item_code and item_description when alclad_code changes
          if (field === "alclad_code") {
            if (trimmedValue) {
              const selectedItem = itemNames?.find((itemOption) => {
                const alcladCode = String(itemOption.alclad_code || "").trim();
                return alcladCode === trimmedValue && alcladCode !== "";
              });
              if (selectedItem) {
                if (selectedItem.item_code) {
                  updatedItem.item_code = String(selectedItem.item_code).trim();
                }
                const desc =
                  selectedItem.item_desc || selectedItem.item_description || "";
                updatedItem.item_description = String(desc || "").trim();
              } else {
                updatedItem.item_code = "";
                updatedItem.item_description = "";
              }
            } else {
              updatedItem.item_code = "";
              updatedItem.item_description = "";
            }
          }

          return updatedItem;
        }
        return item;
      });
      return { ...prev, items: updatedItems };
    });
    if (errors.items?.[index]?.[field]) {
      setErrors((prev) => {
        const newItemsErrors = [...(prev.items || [])];
        if (newItemsErrors[index]) {
          delete newItemsErrors[index][field];
        }
        return { ...prev, items: newItemsErrors };
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      setIsDirty(true);
      const updated = { ...prev, [field]: value };

      // Handle From field changes
      if (field === "og_from") {
        if (value === "Factory") {
          updated.og_source_name = forFactory.factorySource;
          updated.og_source_address = forFactory.factoryDestination;
          updated.og_source_id = "";
        } else {
          updated.og_source_name = "";
          updated.og_source_address = "";
          updated.og_source_id = "";
        }
      }

      // Handle supplier selection
      if (field === "og_source_id") {
        if (value) {
          const selectedSupplier = suppliers?.find(
            (s) => String(s.supplier_code) === String(value) // Updated to match ALERP supplier_code
          );
          if (selectedSupplier) {
            updated.og_source_name = selectedSupplier.supplier_name;
            updated.og_source_address = [
              selectedSupplier.ho_address,
              selectedSupplier.local_address,
            ]
              .filter(Boolean)
              .join(", ");
          }
        } else {
          updated.og_source_id = "";
          updated.og_source_name = "";
          updated.og_source_address = "";
        }
      }

      // Handle coater selection
      if (field === "og_destination_id") {
        if (value) {
          const selectedCoater = coaters?.find(
            (c) => String(c.coater_id) === String(value)
          );
          if (selectedCoater) {
            updated.og_destination_name = selectedCoater.name;
            updated.og_destination_address = selectedCoater.address;
          }
        } else {
          updated.og_destination_id = "";
          updated.og_destination_name = "";
          updated.og_destination_address = "";
        }
      }

      return updated;
    });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const confirmLeave = () => {
    setShowCloseConfirm(false);
    setIsDirty(false);
    onClose();
  };

  const cancelLeave = () => {
    setShowCloseConfirm(false);
  };

  const areAllItemsFilled = () => {
    if (!Array.isArray(formData.items) || formData.items.length === 0) {
      return false;
    }
    return formData.items.every(
      (item) =>
        (item?.item_code || "").trim() &&
        (item?.alclad_code || "").trim() &&
        (item?.item_description || "").trim() &&
        (item?.size || "").toString().trim() &&
        item?.quantity &&
        (item?.og_color_shade || "").trim()
    );
  };

  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          item_code: "",
          alclad_code: "",
          item_description: "",
          size: "",
          quantity: "",
          og_color_shade: "",
        },
      ],
    }));
  };

  const handleDeleteItem = (index) => {
    setFormData((prev) => {
      if (prev.items.length === 1) {
        return {
          ...prev,
          items: [
            {
              item_code: "",
              alclad_code: "",
              item_description: "",
              size: "",
              quantity: "",
              og_color_shade: "",
            },
          ],
        };
      }
      return {
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      };
    });
  };
  const isAddDisabled = !areAllItemsFilled();

  if (!show) return null;

  return (
    <Modal show={show} onHide={handleRequestClose} size="xl" dialogClassName="dialog-inline" centered>
              <style>
    {`
      .dialog-inline {
        max-width: 95% !important;
        width: 95% !important;
      }`}
    </style>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>Edit Outgoing Challan</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form noValidate>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>
                  Challan No <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  className={getInputClass("og_ch_no")}
                  value={formData.og_ch_no}
                  placeholder="Challan Number"
                  onChange={(e) => handleChange("og_ch_no", e.target.value)}
                  readOnly
                />
                {errors.og_ch_no && (
                  <Form.Control.Feedback type="invalid">
                    {errors.og_ch_no}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>
                  Challan Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  className={getInputClass("og_ch_date")}
                  value={formData.og_ch_date}
                  onChange={(e) => handleChange("og_ch_date", e.target.value)}
                />
                {errors.og_ch_date && (
                  <Form.Control.Feedback type="invalid">
                    {errors.og_ch_date}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>From</Form.Label>
                <Form.Select
                  value={formData.og_from}
                  onChange={(e) => handleChange("og_from", e.target.value)}
                >
                  <option value="Factory">Factory</option>
                  <option value="Supplier">Supplier</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>To</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.og_to}
                  onChange={(e) => handleChange("og_to", e.target.value)}
                  disabled
                />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mt-3">Source Details</h6>
          <Row className="g-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>
                  Source Name <span className="text-danger">*</span>
                </Form.Label>
                {formData.og_from === "Factory" ? (
                  <Form.Control
                    type="text"
                    className={getInputClass("og_source_name")}
                    value={formData.og_source_name}
                    onChange={(e) =>
                      handleChange("og_source_name", e.target.value)
                    }
                    disabled
                  />
                ) : (
                  <CustomDropdown
                    options={suppliers}
                    value={formData.og_source_id}
                    onChange={(value) =>
                      handleChange("og_source_id", value.toString())
                    }
                    placeholder="Search suppliers..."
                    isLoading={suppliersLoading}
                    error={errors.og_source_name}
                    idField="supplier_code" // Updated to match ALERP field
                    nameField="supplier_name" // Updated to match ALERP field
                  />
                )}
                {errors.og_source_name && (
                  <Form.Control.Feedback type="invalid">
                    {errors.og_source_name}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Source Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.og_source_address}
                  onChange={(e) =>
                    handleChange("og_source_address", e.target.value)
                  }
                  disabled={
                    formData.og_from === "Factory" || formData.og_source_id
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mt-3">Destination Details</h6>
          <Row className="g-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>
                  Destination Name <span className="text-danger">*</span>
                </Form.Label>
                <CustomDropdown
                  options={coaters}
                  value={formData.og_destination_id}
                  onChange={(value) =>
                    handleChange("og_destination_id", value.toString())
                  }
                  placeholder="Search coaters..."
                  isLoading={coatersLoading}
                  error={errors.og_destination_name}
                  idField="coater_id"
                  nameField="name"
                />
                {errors.og_destination_name && (
                  <Form.Control.Feedback type="invalid">
                    {errors.og_destination_name}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Destination Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.og_destination_address}
                  onChange={(e) =>
                    handleChange("og_destination_address", e.target.value)
                  }
                  disabled={formData.og_destination_id}
                />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mt-3">MIN & PIN Details</h6>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>MIN No</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.min_no}
                  onChange={(e) => handleChange("min_no", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>MIN Date</Form.Label>
                <Form.Control
                  type="date"
                  className={getInputClass("min_date")}
                  value={formData.min_date}
                  onChange={(e) => handleChange("min_date", e.target.value)}
                />
                {errors.min_date && (
                  <Form.Control.Feedback type="invalid">
                    {errors.min_date}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>PIN No</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.pin_no}
                  onChange={(e) => handleChange("pin_no", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>PIN Date</Form.Label>
                <Form.Control
                  type="date"
                  className={getInputClass("pin_date")}
                  value={formData.pin_date}
                  onChange={(e) => handleChange("pin_date", e.target.value)}
                />
                {errors.pin_date && (
                  <Form.Control.Feedback type="invalid">
                    {errors.pin_date}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>

          <hr className="mt-4" />
          <h6>
            Items <span className="text-danger">*</span>
          </h6>
          {formData.items?.map((item, idx) => (
            <div key={idx}>
              <Row className="g-2 mb-2">
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                     Supplier Item Code <span className="text-danger">*</span>
                    </Form.Label>
                    <CustomDropdown
                      options={itemNames || []}
                      value={item.item_code || ""}
                      onChange={(selectedValue) => {
                        handleItemChange(idx, "item_code", selectedValue);
                      }}
                      placeholder="Select Item Code..."
                      isLoading={itemNamesLoading}
                      error={errors.items?.[idx]?.item_code}
                      idField="item_code"
                      nameField="item_code"
                      displayFormat={(option) =>
                        String(option.item_code || "").trim()
                      }
                      isClearable={true}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      ALCLAD Code <span className="text-danger">*</span>
                    </Form.Label>
                    <CustomDropdown
                      options={itemNames || []}
                      value={item.alclad_code || ""}
                      onChange={(selectedValue) => {
                        handleItemChange(idx, "alclad_code", selectedValue);
                      }}
                      placeholder={
                        (item.item_code || "").trim() &&
                        !(item.alclad_code || "").trim()
                          ? "NA"
                          : "Select Alclad Code..."
                      }
                      isLoading={itemNamesLoading}
                      error={errors.items?.[idx]?.alclad_code}
                      idField="alclad_code"
                      nameField="alclad_code"
                      displayFormat={(option) => String(option.alclad_code || "")}
                      isClearable={true}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      Description <span className="text-danger">*</span>
                    </Form.Label>
                    <CustomDropdown
                      options={itemNames || []}
                      value={item.item_description || ""}
                      onChange={(selectedValue) => {
                        handleItemChange(
                          idx,
                          "item_description",
                          selectedValue
                        );
                      }}
                      placeholder={
                        (item.item_code || "").trim() &&
                        !(item.item_description || "").trim()
                          ? "NA"
                          : "Select Description..."
                      }
                      isLoading={itemNamesLoading}
                      error={errors.items?.[idx]?.item_description}
                      idField="item_desc"
                      nameField="item_desc"
                      displayFormat={(option) => {
                        const desc =
                          option.item_desc || option.item_description || "";
                        return String(desc).trim();
                      }}
                      isClearable={true}
                    />
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      Size <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      inputMode="decimal"
                      step="any"
                      placeholder="Size"
                      className={getItemInputClass(idx, "size")}
                      value={item.size}
                      onChange={(e) =>
                        handleItemChange(idx, "size", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      Qty <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      inputMode="numeric"
                      step="1"
                      min="0"
                      placeholder="Qty"
                      className={getItemInputClass(idx, "quantity")}
                      value={item.quantity}
                      onChange={(e) =>
                        handleItemChange(idx, "quantity", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      Color Shade <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Color Shade"
                      className={getItemInputClass(idx, "og_color_shade")}
                      value={item.og_color_shade}
                      onChange={(e) =>
                        handleItemChange(idx, "og_color_shade", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={1} className="d-flex align-items-end">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteItem(idx)}
                    style={{ height: "38px", width: "100%" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                      <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3V2h11v1h-11z" />
                    </svg>
                  </Button>
                </Col>
              </Row>
            </div>
          ))}
          {isAddDisabled ? (
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip id="add-item-disabled-tooltip">
                  Please fill the required fields to  add a new item
                </Tooltip>
              }
            >
              <span className="d-inline-block mt-2" style={{ cursor: "not-allowed" }}>
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={handleAddItem}
                  disabled
                  style={{ pointerEvents: "none" }}
                >
                  + Add Item
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <Button
              variant="outline-success"
              size="sm"
              className="mt-2"
              onClick={handleAddItem}
            >
              + Add Item
            </Button>
          )}

          <hr className="mt-4" />

          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Weight (kg)</Form.Label>
                <Form.Control
                  type="number"
                  className={getInputClass("og_weight_kg")}
                  value={formData.og_weight_kg}
                  onChange={(e) => handleChange("og_weight_kg", e.target.value)}
                />
                {errors.og_weight_kg && (
                  <Form.Control.Feedback type="invalid">
                    {errors.og_weight_kg}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleRequestClose}
          disabled={updateChallanMutation.isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveClick}
          disabled={updateChallanMutation.isLoading}
        >
          {updateChallanMutation.isLoading ||
          updateChallanMutation.isPending ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </Modal.Footer>

      {/* Leave confirmation dialog */}
      <Modal show={showCloseConfirm} onHide={cancelLeave} centered>
        <Modal.Header closeButton>
          <Modal.Title>Leave Form?</Modal.Title>
        </Modal.Header>
        <Modal.Body>Changes you made may not be saved.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelLeave}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmLeave}>
            Leave
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
}

export default EditOutgoingDialog;
